import React, { useState, useEffect, useCallback } from "react";
import {
  useStaffAuth
} from "../context/StaffAuthContext";
import NoticeCard from "../components/NoticeCard";
import {
  X,
  ExternalLink,
  Filter,
  PlusCircle,
  Save
} from "lucide-react";
import {
  createNotice,
  getAllNotices,
  updateNotice,
  pinNotice,
  deleteNotice
} from "../api/noticeApi";
import {
  createNoticePortal,
  getAllNoticesPortal,
  updateNoticePortal,
  pinNoticePortal,
  deleteNoticePortal
} from "../api/noticePortalApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Badge,
  Skeleton
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const CATEGORY_OPTIONS = ["GENERAL", "EXAM", "HOLIDAY", "EVENT", "FEE", "URGENT"];
const AUDIENCE_OPTIONS = ["ALL", "STAFF", "STUDENT", "PARENT"];

const CATEGORY_VARIANTS = {
  GENERAL: "secondary",
  EXAM: "info",
  HOLIDAY: "danger",
  EVENT: "primary",
  FEE: "warning",
  URGENT: "danger",
};

const NoticeBoard = ({ isPortal = false }) => {
  const staffAuth = useStaffAuth();
  
  const canManage = isPortal 
    ? (staffAuth && typeof staffAuth.hasPermission === "function" ? staffAuth.hasPermission("NOTICE_FULL") : false) 
    : true;

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [editingNotice, setEditingNotice] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterAudience, setFilterAudience] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "GENERAL",
    targetAudience: ["ALL"],
    isPinned: false,
    isActive: true,
    expiresAt: "",
    attachmentUrl: "",
  });

  const toast = useToast();

  const triggerBanner = (type, message) => {
    if (type === "success") {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const loadNotices = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        category: filterCategory,
        targetAudience: filterAudience,
        ...( !isPortal && { isActive: filterActive } )
      };

      const res = isPortal ? await getAllNoticesPortal(filters) : await getAllNotices(filters);
      const dataList = Array.isArray(res) ? res : res.notices || [];
      
      const sorted = [...dataList].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotices(sorted);
    } catch (err) {
      triggerBanner("error", "Failed to fetch notices");
    } finally {
      setLoading(false);
    }
  }, [isPortal, filterCategory, filterAudience, filterActive]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAudienceChange = (audience) => {
    setFormData((prev) => {
      let updatedAudience = [...prev.targetAudience];
      if (updatedAudience.includes(audience)) {
        updatedAudience = updatedAudience.filter((a) => a !== audience);
      } else {
        updatedAudience.push(audience);
      }
      if (updatedAudience.length === 0) updatedAudience = ["ALL"];
      return { ...prev, targetAudience: updatedAudience };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      triggerBanner("error", "Title and content fields are required.");
      return;
    }

    try {
      const payload = {
        ...formData,
        expiresAt: formData.expiresAt || null,
      };

      if (editingNotice) {
        isPortal 
          ? await updateNoticePortal(editingNotice._id, payload)
          : await updateNotice(editingNotice._id, payload);
        triggerBanner("success", "Notice updated successfully!");
      } else {
        isPortal
          ? await createNoticePortal(payload)
          : await createNotice(payload);
        triggerBanner("success", "Notice published successfully!");
      }

      handleResetForm();
      loadNotices();
    } catch (err) {
      triggerBanner("error", err?.response?.data?.message || "Failed to process transaction");
    }
  };

  const handleEditInit = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      category: notice.category,
      targetAudience: notice.targetAudience || ["ALL"],
      isPinned: notice.isPinned || false,
      isActive: notice.isActive !== undefined ? notice.isActive : true,
      expiresAt: notice.expiresAt ? notice.expiresAt.split("T")[0] : "",
      attachmentUrl: notice.attachmentUrl || "",
    });
  };

  const handleResetForm = () => {
    setEditingNotice(null);
    setFormData({
      title: "",
      content: "",
      category: "GENERAL",
      targetAudience: ["ALL"],
      isPinned: false,
      isActive: true,
      expiresAt: "",
      attachmentUrl: "",
    });
  };

  const handleTogglePin = async (id) => {
    setNotices((prev) =>
      prev.map((n) => (n._id === id ? { ...n, isPinned: !n.isPinned } : n))
        .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))
    );

    try {
      isPortal ? await pinNoticePortal(id) : await pinNotice(id);
      loadNotices();
    } catch (err) {
      triggerBanner("error", "Failed to update pin alignment status");
      loadNotices();
    }
  };

  const handleDelete = async (id) => {
    try {
      isPortal ? await deleteNoticePortal(id) : await deleteNotice(id);
      triggerBanner("success", "Notice deleted permanently.");
      if (selectedNotice?._id === id) setSelectedNotice(null);
      loadNotices();
    } catch (err) {
      triggerBanner("error", "Could not remove notice entry");
    }
  };

  const formatDateString = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 antialiased">
      {/* Banners */}
      
      

      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* Main Feed Column */}
        <div className="flex-1 order-2 lg:order-1 space-y-4">
          
          {/* Filters Card */}
          <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3">
              <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
                Filter Bulletin Entries
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">All Categories</option>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">Target Audience</label>
                  <select
                    value={filterAudience}
                    onChange={(e) => setFilterAudience(e.target.value)}
                    className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">All Viewers</option>
                    {AUDIENCE_OPTIONS.map((aud) => (
                      <option key={aud} value={aud}>{aud}</option>
                    ))}
                  </select>
                </div>

                {!isPortal && (
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">Status</label>
                    <select
                      value={filterActive}
                      onChange={(e) => setFilterActive(e.target.value)}
                      className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">All States</option>
                      <option value="true">Active Only</option>
                      <option value="false">Inactive Only</option>
                    </select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Feed Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-2">
            <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
              Active Bulletins ({notices.length})
            </span>
          </div>

          {/* Cards List / Loading */}
          {loading && notices.length === 0 ? (
            <div className="space-y-3">
              <Skeleton height={120} width="100%" />
              <Skeleton height={120} width="100%" />
            </div>
          ) : notices.length === 0 ? (
            <Card className="border border-dashed border-neutral-200/80 shadow-none p-10 text-center">
              <CardContent className="p-0 text-xs text-neutral-400 dark:text-neutral-500">
                No notices published matching current filter criteria.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {notices.map((notice) => (
                <NoticeCard
                  key={notice._id}
                  notice={notice}
                  canManage={canManage}
                  onEdit={handleEditInit}
                  onDelete={handleDelete}
                  onPin={handleTogglePin}
                  onClick={setSelectedNotice}
                />
              ))}
            </div>
          )}
        </div>

        {/* Form Sidebar */}
        {canManage && (
          <div className="w-full lg:w-80 order-1 lg:order-2 shrink-0">
            <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900 sticky top-6">
              <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3">
                <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
                  {editingNotice ? <Save className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" /> : <PlusCircle className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />}
                  {editingNotice ? "Edit Bulletin Entry" : "Post New Notice"}
                </CardTitle>
              </CardHeader>

              <CardContent className="p-4 space-y-3">
                <form onSubmit={handleFormSubmit} className="space-y-3">
                  <Input
                    label="Title *"
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter notice title"
                  />

                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                      Content *
                    </label>
                    <textarea
                      name="content"
                      required
                      rows={4}
                      value={formData.content}
                      onChange={handleInputChange}
                      placeholder="Write message details..."
                      className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {CATEGORY_OPTIONS.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Target Audience</label>
                    <div className="grid grid-cols-2 gap-2 bg-neutral-50/80 p-2.5 rounded-md border border-neutral-200 dark:border-neutral-800">
                      {AUDIENCE_OPTIONS.map((option) => (
                        <label key={option} className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={formData.targetAudience.includes(option)}
                            onChange={() => handleAudienceChange(option)}
                            className="rounded border-neutral-300 dark:border-neutral-700 text-primary-600 dark:text-primary-400 focus:ring-primary-500 h-3.5 w-3.5"
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>

                  <Input
                    label="Expiry Date (Optional)"
                    type="date"
                    name="expiresAt"
                    value={formData.expiresAt}
                    onChange={handleInputChange}
                  />

                  <Input
                    label="Attachment URL (Optional)"
                    type="url"
                    name="attachmentUrl"
                    value={formData.attachmentUrl}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />

                  <div className="space-y-1.5 pt-1">
                    <label className="flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isPinned"
                        checked={formData.isPinned}
                        onChange={handleInputChange}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
                      />
                      Pin notice to top
                    </label>

                    <label className="flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
                      />
                      Active / Publish Visible
                    </label>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      className="flex-1 text-xs font-semibold py-2"
                    >
                      {editingNotice ? "Update Notice" : "Publish Notice"}
                    </Button>

                    {editingNotice && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleResetForm}
                        className="text-xs py-2 border border-neutral-200 dark:border-neutral-800"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* DETAIL MODAL */}
      {selectedNotice && (
        <div className="fixed inset-0 bg-neutral-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-white dark:bg-neutral-900 shadow-lg border border-neutral-200/80 max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setSelectedNotice(null)}
              className="absolute top-4 right-4 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 p-1 rounded-md transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 pr-12 pb-4">
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                <Badge variant={CATEGORY_VARIANTS[selectedNotice.category] || "secondary"} className="text-[10px]">
                  {selectedNotice.category}
                </Badge>
                {selectedNotice.targetAudience?.map((audience) => (
                  <Badge key={audience} variant="secondary" className="text-[10px]">
                    {audience}
                  </Badge>
                ))}
                {selectedNotice.isPinned && (
                  <Badge variant="warning" className="text-[10px]">
                    Pinned
                  </Badge>
                )}
              </div>
              <CardTitle className="text-lg font-bold text-neutral-900 dark:text-neutral-100 leading-snug">
                {selectedNotice.title}
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              <div className="bg-neutral-50 dark:bg-neutral-950 p-4 rounded-lg border border-neutral-100 dark:border-neutral-800 max-h-72 overflow-y-auto">
                <p className="text-xs text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                  {selectedNotice.content}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-[11px] border-y border-neutral-100 dark:border-neutral-800 py-3 text-neutral-500 dark:text-neutral-400">
                <div>
                  <span className="block font-semibold text-neutral-400 dark:text-neutral-500 uppercase text-[9px]">Posted By</span>
                  <span className="text-neutral-800 dark:text-neutral-100 font-medium">{selectedNotice.createdBy?.name || "System"}</span>
                </div>
                <div>
                  <span className="block font-semibold text-neutral-400 dark:text-neutral-500 uppercase text-[9px]">Published Date</span>
                  <span className="text-neutral-800 dark:text-neutral-100 font-medium">{formatDateString(selectedNotice.createdAt)}</span>
                </div>
                {selectedNotice.expiresAt && (
                  <div className="col-span-2">
                    <span className="block font-semibold text-neutral-400 dark:text-neutral-500 uppercase text-[9px]">Expiration Date</span>
                    <span className="text-neutral-800 dark:text-neutral-100 font-medium">{formatDateString(selectedNotice.expiresAt)}</span>
                  </div>
                )}
              </div>

              {selectedNotice.attachmentUrl && (
                <div>
                  <a
                    href={selectedNotice.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium bg-primary-50 px-3 py-2 rounded-md border border-primary-100"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View Document Attachment
                  </a>
                </div>
              )}

              {canManage && (
                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      handleEditInit(selectedNotice);
                      setSelectedNotice(null);
                    }}
                    className="text-xs border-neutral-200 dark:border-neutral-800"
                  >
                    Edit Notice
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this notice?")) {
                        handleDelete(selectedNotice._id);
                      }
                    }}
                    className="text-xs"
                  >
                    Delete Notice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NoticeBoard;